import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormHelperText,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useRef, useState } from 'react'
import TruncatedText from './TruncatedText'
import { AddRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

export interface SelectItem {
    id: string
    name: string
    [key: string]: any
}

interface SearchAndMultiSelectInputProps {
    id: string
    label: string
    value: string[] // array of ids
    placeholder?: string
    onSelect: (id: string, values: string[]) => void
    list: SelectItem[]
    required?: boolean
    disabled?: boolean
    isLegend?: boolean
    list_Height?: string
    hasAddNew?: boolean
    onClickAddNew?: () => void
    onClickRowIcon?: (item: SelectItem) => void
    errorMessage?: string
    ui_width?: string
    allowClear?: boolean
    autoFocusOnMount?: boolean
    isFetching?: boolean
    showLabel?: boolean
    stopKeyboardPropagation?: boolean
}

const SearchAndMultiSelectInput = ({
    id,
    label,
    value,
    placeholder,
    onSelect,
    list,
    required = false,
    disabled = false,
    isLegend = false,
    list_Height = '250px',
    hasAddNew = false,
    onClickAddNew = () => {},
    onClickRowIcon,
    errorMessage = '',
    ui_width,
    allowClear = true,
    autoFocusOnMount = false,
    isFetching = false,
    showLabel = true,
    stopKeyboardPropagation = false,
}: SearchAndMultiSelectInputProps): ReactElement => {
    const { t } = useTranslation()
    const addNewOption = {
        id: 'ADD_NEW',
        name: '',
        hasItemEdit: false,
    }

    const fullList = hasAddNew ? [addNewOption, ...list] : list
    const selectedObjects = fullList.filter((item) => value.includes(item.id))
    const [searchText, setSearchText] = useState('')
    const [isOpen, setIsOpen] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const [isFocused, setIsFocused] = useState(false)

    const handleFocus = () => setIsFocused(true)
    const handleBlur = () => {
        setSearchText('')
        setIsFocused(false)
    }

    const handleChange = (_event: any, newValues: SelectItem[]) => {
        const updateValue = newValues?.map((v) => v.id)
        onSelect(id, updateValue)
        setSearchText('')
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (
            stopKeyboardPropagation &&
            [
                'ArrowDown',
                'ArrowUp',
                'ArrowRight',
                'ArrowLeft',
                'Enter',
            ].includes(e.key)
        ) {
            e.stopPropagation()
        }
        // Prevent deleting selected item on backspace when input is empty
        if (e.key === 'Backspace' && inputRef.current?.value === '') {
            e.stopPropagation()
            e.preventDefault()
        }
    }
    const handleRowIcon = (e: any, item: any) => {
        e.stopPropagation()
        setIsOpen(false)
        setIsFocused(false)
        inputRef.current?.blur()
        onClickRowIcon?.(item)
    }

    const handleAddNewClick = () => {
        setIsOpen(false)
        setIsFocused(false)
        inputRef.current?.blur()
        onClickAddNew?.()
    }

    const getPlaceholderText = () => {
        if (selectedObjects.length) {
            return isFocused ? t('search') : ''
        }
        return placeholder || t('selectLabel', { label })
    }

    useEffect(() => {
        if (autoFocusOnMount) inputRef.current?.focus()
    }, [autoFocusOnMount])

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={disabled} size="small">
                {!isLegend && showLabel && (
                    <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
                        {label}
                        {required && '*'}
                    </Typography>
                )}

                <Autocomplete
                    id={id}
                    openOnFocus
                    open={isOpen}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                    inputValue={searchText}
                    onInputChange={(_e, newInput, reason) => {
                        if (reason === 'selectOption') return
                        setSearchText(newInput)
                    }}
                    selectOnFocus={false}
                    clearOnBlur={false}
                    autoHighlight={false}
                    handleHomeEndKeys
                    multiple
                    disableCloseOnSelect
                    options={fullList}
                    value={selectedObjects}
                    getOptionLabel={(option) => option.name}
                    onChange={handleChange}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    disabled={disabled}
                    disableClearable={!allowClear}
                    ListboxProps={{
                        style: { maxHeight: list_Height },
                    }}
                    loading={isFetching}
                    noOptionsText={''}
                    filterOptions={(options, state) => {
                        const input = state.inputValue || ''
                        return options.filter(
                            (option) =>
                                option.id === 'ADD_NEW' ||
                                option.name
                                    .toLowerCase()
                                    .includes(input.toLowerCase()),
                        )
                    }}
                    slotProps={{
                        clearIndicator: {
                            id: `${id}-clear-button`,
                            sx: { padding: 0 },
                        },
                        popupIndicator: {
                            id: `${id}-dropdown-button`,
                        },
                    }}
                    renderOption={(props, option, { selected }) =>
                        option.id === 'ADD_NEW' ? (
                            <Box
                                component="li"
                                id={id + 'add_new'}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    cursor: 'pointer',
                                    padding: '2px 10px 5px 15px',
                                    '& :hover': {
                                        background: '#f9f9f9',
                                    },
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleAddNewClick()
                                }}
                            >
                                <AddRounded color={'primary'} />
                                <Box
                                    sx={{
                                        marginLeft: '5px',
                                    }}
                                >
                                    <Typography
                                        fontWeight="600"
                                        fontSize="14px"
                                    >
                                        {t('addNew')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                component="li"
                                {...props}
                                key={option.id}
                                sx={{
                                    fontSize: '14px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: ui_width || 'auto',
                                    padding: '7px 15px 7px 10px',
                                    '&:hover .row-icon': {
                                        display: 'flex',
                                    },
                                }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width={
                                        onClickRowIcon
                                            ? 'calc(100% - 20px)'
                                            : '100%'
                                    }
                                >
                                    <Checkbox
                                        size="small"
                                        checked={selected}
                                        sx={{
                                            padding: '0px',
                                            paddingRight: '5px',
                                        }}
                                    />
                                    <TruncatedText text={option.name} />
                                </Box>
                                {option.hasItemEdit && option.id !== '' && (
                                    <IconButton
                                        id={`${id}-${option.name}-edit`}
                                        onClick={(e) =>
                                            handleRowIcon(e, option)
                                        }
                                        aria-haspopup="true"
                                        key="edit"
                                        disabled={false}
                                        sx={{
                                            padding: '0px',
                                            marginLeft: 'auto',
                                            display: 'none',
                                        }}
                                        className="row-icon"
                                    >
                                        <img
                                            src="/assets/images/icons/edit.svg"
                                            alt="Edit"
                                        />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    }
                    renderInput={(params) => (
                        <Tooltip
                            title={selectedObjects
                                .map((s) => s.name)
                                .join(', ')}
                            arrow
                            placement="top"
                        >
                            <TextField
                                {...params}
                                id={`${id}-input`}
                                inputRef={inputRef}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                label={
                                    isLegend
                                        ? `${label}${required ? ' *' : ''}`
                                        : ''
                                }
                                size="small"
                                placeholder={getPlaceholderText()}
                                error={!!errorMessage}
                                onKeyDown={handleKeyDown}
                                InputProps={{
                                    ...params.InputProps,
                                    style: {
                                        fontSize: 14,
                                        paddingLeft: '14px', // Remove padding inside input box
                                        display: 'flex', // Keep everything in one line
                                        alignItems: 'center',
                                        flexWrap: 'nowrap',
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                    renderTags={(value) => (
                        <Typography
                            sx={{
                                fontSize: '14px',
                                opacity: value.length ? 1 : 0.8,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'inline-block',
                                marginRight: '8px',
                            }}
                        >
                            {value.map((v) => v.name).join(', ')}
                        </Typography>
                    )}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            padding: '2.5px 4px 2.5px 5px', // Adjust padding
                            display: 'flex', // Align input and values horizontally
                            alignItems: 'center', // Ensure center alignment
                            '& input::placeholder': {
                                opacity: 0.2,
                            },
                        },
                        '& .MuiAutocomplete-input': {
                            paddingLeft: '0px !important',
                            minWidth: isFocused
                                ? '80px !important'
                                : '0 !important',
                        },
                        '& > div > div': {
                            paddingRight: '44px !important', // Adjust padding for dropdown & close icon that appears on hover
                        },
                    }}
                />

                {errorMessage && (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default SearchAndMultiSelectInput
